import React, { useId, useState } from 'react';
import Icon from '../../shared/Icon';
import Select from '../../shared/Select';
import Checkbox from '../../shared/Checkbox';
import SecretInput from '../SecretInput';
import { Optional, Required } from '../FieldMarks';
import { ORG_DATA_REGIONS, ORG_SESSION_TIMEOUTS, ORG_TIME_ZONES } from '../../../../models/SettingsModel';
import { useSettingsState } from '../../../../viewModels/useSettingsModel';
import './Organization.css';

const NO_SSO = { tenantId: '', clientId: '', clientSecret: '' };

const formOf = (org) => ({
  name: org.name,
  timeZone: org.timeZone,
  dataRegion: org.dataRegion,
  sessionTimeout: org.sessionTimeout,
  rememberEmail: org.rememberEmail,
  ...NO_SSO,
});

/**
 * Settings > Organization (Navy Phase 0, story 30): the organization profile
 * and its authentication, edited in place, with no Edit mode to enter. Update
 * organization and Cancel stay disabled until something changes. The SSO
 * values are never stored: they live only in the open form, as they would
 * behind a real secrets API, so a save or a cancel clears them.
 */
function OrganizationSection({ model, onShowToast }) {
  const { organization } = useSettingsState(model);
  const ids = useId();
  const [form, setForm] = useState(() => formOf(organization));
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const saved = formOf(organization);
  const changed = Object.keys(form).some((key) => form[key] !== saved[key]);
  const complete = !!form.name.trim();

  const cancel = () => {
    setForm(saved);
    onShowToast?.('Changes discarded.', 'info');
  };

  const save = () => {
    if (!changed || !complete) return;
    const { tenantId, clientId, clientSecret, ...fields } = form;
    setForm(formOf(model.saveOrganization(fields)));
    onShowToast?.('Organization updated.', 'success');
  };

  return (
    <>
      <section className="navy-settings-section" aria-labelledby={`${ids}-profile`}>
        <div className="navy-settings-head">
          <h2 className="navy-settings-title" id={`${ids}-profile`}>Organization profile</h2>
        </div>
        <div className="library-panel">
          <div className="resui-field navy-settings-field">
            <span className="resui-field-label">Organization logo<Optional /></span>
            <span className="settings-org-logo-preview" aria-hidden="true"><Icon name="business" /></span>
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-name`}>Organization name<Required /></label>
            <input
              id={`${ids}-name`}
              type="text"
              className="resui-input"
              value={form.name}
              onChange={(e) => set('name')(e.target.value)}
            />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-zone`}>Primary time zone<Required /></label>
            <Select id={`${ids}-zone`} className="resui-select-field--block" value={form.timeZone} options={ORG_TIME_ZONES} onChange={set('timeZone')} />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-region`}>Default data region<Required /></label>
            <Select id={`${ids}-region`} className="resui-select-field--block" value={form.dataRegion} options={ORG_DATA_REGIONS} onChange={set('dataRegion')} />
          </div>
        </div>
      </section>

      <section className="navy-settings-section" aria-labelledby={`${ids}-auth`}>
        <div className="navy-settings-head">
          <h2 className="navy-settings-title" id={`${ids}-auth`}>Authentication</h2>
        </div>
        <div className="library-panel">
          <h3 className="org-setting-name">Single sign-on (SSO)</h3>
          <p className="org-setting-note">Microsoft Entra ID</p>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-tenant`}>Tenant ID<Optional /></label>
            <SecretInput id={`${ids}-tenant`} value={form.tenantId} onChange={(e) => set('tenantId')(e.target.value)} placeholder="Microsoft Entra tenant ID (UUID)" />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-client`}>Client ID<Optional /></label>
            <SecretInput id={`${ids}-client`} value={form.clientId} onChange={(e) => set('clientId')(e.target.value)} placeholder="Microsoft Entra application (client) ID" />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-secret`}>Client secret<Optional /></label>
            <SecretInput id={`${ids}-secret`} value={form.clientSecret} onChange={(e) => set('clientSecret')(e.target.value)} placeholder="Leave blank to keep existing" />
          </div>
          <div className="resui-field navy-settings-field">
            <label className="resui-field-label" htmlFor={`${ids}-timeout`}>Session timeout<Required /></label>
            <Select
              id={`${ids}-timeout`}
              className="resui-select-field--block"
              value={form.sessionTimeout}
              options={ORG_SESSION_TIMEOUTS}
              onChange={set('sessionTimeout')}
              describedBy={`${ids}-timeout-help`}
              scale
            />
            <span id={`${ids}-timeout-help`} className="resui-field-helper">Sign users out after a period of inactivity</span>
          </div>
          <div className="resui-field navy-settings-field">
            <Checkbox checked={form.rememberEmail} onChange={set('rememberEmail')} ariaDescribedBy={`${ids}-remember-help`}>
              Remember email
            </Checkbox>
            <span id={`${ids}-remember-help`} className="resui-field-helper org-check-help">Pre-fill a returning user&apos;s email at sign-in</span>
          </div>
        </div>
      </section>

      <div className="navy-settings-actions">
        <button type="button" className="resui-btn resui-btn--secondary" disabled={!changed} onClick={cancel}>Cancel</button>
        <button type="button" className="resui-btn resui-btn--primary" disabled={!changed || !complete} onClick={save}>Update organization</button>
      </div>
    </>
  );
}

export default OrganizationSection;
